<script>
  import { onMount } from "svelte";
  import { formatDate } from "../../services/utilities.js";
  import { getOrdersBySku } from "../../models/admin/orders.js";
  import { getProduct } from "../../models/admin/product.js";
  import {
    listReturnConditionTypes,
    getOrderReturnConditions,
    reportMissingPieces,
    deleteOrderReturnCondition,
  } from "../../models/admin/returnConditions.js";
  import Alert from "../../components/Alert.svelte";

  export let preselectedSku = null;
  export let preselectedTarget = null; // { orderID, orderItemID }
  export let preselectedSource = null;
  // launchingItem: when set, the modal will look up existing Missing Pieces
  // conditions on this order_item after the search runs and pre-select the
  // matching side (Reported / Caused) so the admin sees the current state.
  export let launchingItem = null; // { orderID, orderItemID }

  let sku = preselectedSku || "";
  let orders = [];
  let loading = false;
  let hasSearched = false;
  let error = null;
  let successMsg = null;
  let submitting = false;

  let conditionTypeMap = {}; // { [id]: ReturnConditionType }
  let orderConditions = {};  // { [orderID]: [OrderReturnCondition] }
  let product = null;        // fetched product details for the SKU preview

  // Radio group state: a single string key per side. Using bind:group ensures
  // the radio's checked attribute stays in sync when we clear selections, which
  // a one-way `checked={expr}` does not reliably do.
  let targetKey = preselectedTarget
    ? `${preselectedTarget.orderID}|${preselectedTarget.orderItemID}`
    : null;
  let sourceKey = preselectedSource
    ? `${preselectedSource.orderID}|${preselectedSource.orderItemID}`
    : null;

  function parseKey(k) {
    if (!k) return null;
    const [oid, iid] = k.split("|").map(Number);
    return { orderID: oid, orderItemID: iid };
  }

  $: selectedTarget = parseKey(targetKey);
  $: selectedSource = parseKey(sourceKey);

  // Called from each radio's on:change. The bind:group has already updated the
  // tapped side's key by the time this fires, so we just need to clear the
  // OTHER side if it points to the same item. This keeps the latest click as
  // the winning selection.
  function clearOppositeIfSame(role, key) {
    if (role === "target" && sourceKey === key) sourceKey = null;
    if (role === "source" && targetKey === key) targetKey = null;
  }

  let count = 0;

  async function loadOrders() {
    error = null;
    successMsg = null;
    if (!sku) return;
    loading = true;
    product = null;
    try {
      const [ordersRes, productRes] = await Promise.all([
        getOrdersBySku(sku, 10),
        getProduct(sku).catch(() => null),
      ]);
      product = productRes || null;
      orders = (ordersRes && ordersRes.orders) || [];
      hasSearched = true;
      // Fetch existing return conditions for each order in parallel.
      orderConditions = {};
      await Promise.all(
        orders.map(async (o) => {
          try {
            const existing = await getOrderReturnConditions(o.id);
            orderConditions = {
              ...orderConditions,
              [o.id]: Array.isArray(existing) ? existing : [],
            };
          } catch (_) {
            orderConditions = { ...orderConditions, [o.id]: [] };
          }
        })
      );
      applyExistingPreselect();
    } catch (e) {
      error = (e && e.message) || "Failed to load orders";
      hasSearched = true;
    } finally {
      loading = false;
    }
  }

  // Mirror existing missing-piece attributions into the radio state so the form
  // reflects current reality on open. A Reported/Caused pair often lives on two
  // different orders (e.g. the reporter's order and the prior renter's), so we
  // scan every matched order's conditions rather than only the launching item's
  // order. The launching item still wins its side if multiple rows exist.
  function applyExistingPreselect() {
    let target = preselectedTarget ? targetKey : null;
    let source = preselectedSource ? sourceKey : null;

    for (const oid of Object.keys(orderConditions)) {
      for (const c of orderConditions[oid] || []) {
        if (c.order_item_id == null) continue;
        const t = conditionTypeMap[c.condition_type_id];
        if (!t) continue;
        const key = `${oid}|${c.order_item_id}`;
        const isLaunching =
          launchingItem &&
          oid === String(launchingItem.orderID) &&
          c.order_item_id === launchingItem.orderItemID;
        if (t.name === "Missing Pieces (Reported)") {
          if (target == null || isLaunching) target = key;
        } else if (t.name === "Missing Pieces (Caused)") {
          if (source == null || isLaunching) source = key;
        }
      }
    }

    if (target != null) targetKey = target;
    if (source != null) sourceKey = source;
  }

  // Returns the first order_item on this order matching the SKU we searched for.
  function matchedItem(order) {
    const list = (order && order.order_products) || [];
    return list.find((p) => p.sku === sku) || null;
  }

  // The two missing-piece condition type names are uneditable and stable.
  function isMissingPieceType(t) {
    return t && (t.name === "Missing Pieces (Reported)" || t.name === "Missing Pieces (Caused)");
  }

  // Find any missing-piece condition rows on this order that target this order_item.
  function missingPieceRowsForItem(orderID, orderItemID) {
    const conds = orderConditions[orderID] || [];
    return conds.filter(
      (c) =>
        c.order_item_id === orderItemID &&
        isMissingPieceType(conditionTypeMap[c.condition_type_id])
    );
  }

  // Derived set of "orderID|orderItemID" keys that currently have at least one
  // missing-piece condition. Svelte recomputes this whenever orderConditions or
  // conditionTypeMap change, which keeps the per-row Clear button reactive.
  $: missingPieceItemKeys = (() => {
    const out = new Set();
    for (const oid of Object.keys(orderConditions)) {
      for (const c of orderConditions[oid] || []) {
        if (c.order_item_id != null && isMissingPieceType(conditionTypeMap[c.condition_type_id])) {
          out.add(`${oid}|${c.order_item_id}`);
        }
      }
    }
    return out;
  })();

  // Per-row clearing-in-progress flag, keyed by `${orderID}|${orderItemID}`.
  let clearing = {};

  async function clearMissingPiecesForItem(order, item) {
    const rows = missingPieceRowsForItem(order.id, item.order_product_id);
    if (rows.length === 0) return;
    const k = itemKey(order.id, item.order_product_id);
    clearing = { ...clearing, [k]: true };
    try {
      await Promise.all(rows.map((r) => deleteOrderReturnCondition(r.id)));
      // Refresh this order's conditions so the badges + preselect reflect reality.
      const updated = await getOrderReturnConditions(order.id);
      orderConditions = {
        ...orderConditions,
        [order.id]: Array.isArray(updated) ? updated : [],
      };
      // If the radios were pointing at this item, drop them — the underlying
      // attribution that justified the preselect is gone.
      if (targetKey === k) targetKey = null;
      if (sourceKey === k) sourceKey = null;
    } catch (e) {
      error = (e && e.message) || "Failed to clear missing-piece conditions";
    } finally {
      clearing = { ...clearing, [k]: false };
    }
  }

  // Returns "Customer Name · Order #N" for a selected order, falling back
  // gracefully if the order is no longer in the current results.
  function selectionLabel(sel) {
    if (!sel) return null;
    const order = orders.find((o) => o.id === sel.orderID);
    const name = (order && order.customer_name) || (order && order.email) || "Unknown customer";
    return `${name} · Order #${sel.orderID}`;
  }

  function productImageUrl(p) {
    if (!p || !Array.isArray(p.images) || p.images.length === 0) return null;
    const img = p.images[0];
    if (!img || !img.url) return null;
    return img.url + img.filename + "-sm." + img.extension;
  }


  function clearSelection(role) {
    if (role === "target") targetKey = null;
    else sourceKey = null;
  }

  function itemKey(orderID, orderItemID) {
    return `${orderID}|${orderItemID}`;
  }

  async function submit() {
    error = null;
    successMsg = null;
    if (!sku) {
      error = "SKU is required.";
      return;
    }
    if (!selectedTarget && !selectedSource) {
      error = "Pick at least one item — either Reported by or Previously rented by.";
      return;
    }
    submitting = true;
    try {
      const req = { sku, count };
      if (selectedTarget) {
        req.target_order_id = selectedTarget.orderID;
        req.target_order_item_id = selectedTarget.orderItemID;
      }
      if (selectedSource) {
        req.source_order_id = selectedSource.orderID;
        req.source_order_item_id = selectedSource.orderItemID;
      }
      const ok = await reportMissingPieces(req);
      if (ok === true || ok === undefined) {
        successMsg = "Recorded.";
        targetKey = null;
        sourceKey = null;
        count = 0;
        sku = "";
        product = null;
        orders = [];
        orderConditions = {};
        hasSearched = false;
      } else {
        error = "Submit failed.";
      }
    } catch (e) {
      error = (e && e.message) || "Submit failed.";
    } finally {
      submitting = false;
    }
  }

  onMount(async () => {
    const types = await listReturnConditionTypes();
    if (Array.isArray(types)) {
      conditionTypeMap = types.reduce((acc, t) => ({ ...acc, [t.id]: t }), {});
    }
    if (sku) loadOrders();
  });
</script>

<section>
  {#if error}
    <Alert type="danger" icon="warning">{error}</Alert>
  {:else if successMsg}
    <Alert type="success" icon="check">{successMsg}</Alert>
  {/if}

  <div class="search-form">
    <h3>Search Orders by SKU</h3>
    <p class="hint">
      Pick one item to mark <strong>Reported by</strong> (the customer who reported the
      missing pieces) and/or one item to mark <strong>Previously rented by</strong>
      (a prior renter who may have caused it).
    </p>
    <div class="form-row">
      <div class="form-field">
        <label for="sku-input">SKU</label>
        <input
          id="sku-input"
          class="form-input form-input-sm"
          type="text"
          bind:value={sku}
          placeholder="e.g. ABC-123"
          on:keydown={(e) => e.key === "Enter" && loadOrders()}
        />
      </div>
      <div class="form-field">
        <button class="btn" on:click={loadOrders} disabled={!sku || loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </div>
    </div>

    {#if product}
      <div class="product-preview">
        {#if productImageUrl(product)}
          <img
            class="product-image"
            src={productImageUrl(product)}
            alt={product.title || sku}
          />
        {/if}
        <div class="product-meta">
          <p class="product-title">{product.title || "(untitled)"}</p>
          <p class="hint">
            SKU
            <a
              href="/admin/inventory/product/{product.sku || sku}"
              target="_blank"
              rel="noopener"
            >{product.sku || sku}</a>
            {#if product.product_type && product.product_type.name}
              · {product.product_type.name}
            {/if}
          </p>
        </div>
      </div>
    {/if}
  </div>

  <div class="selection-bar">
    <div class="selection-row">
      <span class="selection-label">Reported by</span>
      {#if selectedTarget}
        <span class="selection-value">{selectionLabel(selectedTarget)}</span>
        <button
          class="selection-clear"
          title="Clear selection"
          on:click={() => clearSelection("target")}
        >×</button>
      {:else}
        <span class="selection-empty">Not selected</span>
      {/if}
    </div>
    <div class="selection-row">
      <span class="selection-label">Previously rented by</span>
      {#if selectedSource}
        <span class="selection-value">{selectionLabel(selectedSource)}</span>
        <button
          class="selection-clear"
          title="Clear selection"
          on:click={() => clearSelection("source")}
        >×</button>
      {:else}
        <span class="selection-empty">Not selected</span>
      {/if}
    </div>
    <div class="selection-row">
      <label class="selection-label" for="missing-piece-count">Pieces missing</label>
      <input
        id="missing-piece-count"
        class="form-input form-input-sm count-input"
        type="number"
        min="0"
        bind:value={count}
      />
    </div>
    <div class="selection-row submit-row">
      <button
        class="btn btn-primary"
        on:click={submit}
        disabled={submitting || (!selectedTarget && !selectedSource)}
      >
        {submitting ? "Submitting..." : "Submit"}
      </button>
    </div>
  </div>

  <h3>Matching Orders</h3>
  {#if loading}
    <p class="hint">Loading…</p>
  {:else if !hasSearched}
    <p class="hint">Enter a SKU above and click Search to find orders.</p>
  {:else if orders.length === 0}
    <p class="hint">No orders found with sku "{sku}".</p>
  {:else}
    <div class="orders-table-wrap">
    <table class="table padding-xs orders-table">
      <thead>
        <tr>
          <th>Order</th>
          <th>Status</th>
          <th>Customer</th>
          <th>Email</th>
          <th>Ordered</th>
          <th>Last updated</th>
          <th class="col-num">Qty</th>
          <th class="col-num">Returned</th>
          <th>Existing conditions</th>
          <th>Reported by</th>
          <th>Previously rented by</th>
        </tr>
      </thead>
      <tbody>
        {#each orders as order (order.id)}
          {@const item = matchedItem(order)}
          <tr>
            <td class="col-order">#{order.id}</td>
            <td class="col-status">{order.status || "—"}</td>
            <td class="col-customer">{order.customer_name || "—"}</td>
            <td class="col-email">{order.email || order.customer_id || "—"}</td>
            <td class="col-date">{formatDate(order.created_at, { showTime: false })}</td>
            <td class="col-date">
              {#if order.updated_at}
                {formatDate(order.updated_at, { showTime: false })}
              {:else}
                <span class="hint">—</span>
              {/if}
            </td>
            <td class="col-num">{item ? item.quantity : "—"}</td>
            <td class="col-num">
              {#if !item}
                <span class="hint">—</span>
              {:else if item.returned_quantity >= item.quantity}
                Yes
              {:else if item.returned_quantity > 0}
                {item.returned_quantity}/{item.quantity}
              {:else}
                No
              {/if}
            </td>
            <td class="conditions-cell">
              {#if orderConditions[order.id] && orderConditions[order.id].length > 0}
                {#each orderConditions[order.id] as c (c.id)}
                  {@const t = conditionTypeMap[c.condition_type_id]}
                  <span
                    class="score-badge score-badge-warning"
                    title={c.count > 0 ? `${t ? t.name : "Condition"}: ${c.count}` : (t ? t.name : "Condition")}
                  >
                    {#if t && t.icon}
                      <svg class="icon"><use href="/icons.svg#{t.icon}" /></svg>
                    {/if}
                    {t ? t.name : `Type #${c.condition_type_id}`}
                    {#if c.count > 0}· {c.count}{/if}
                  </span>
                {/each}
                {#if item && missingPieceItemKeys.has(itemKey(order.id, item.order_product_id))}
                  <button
                    class="btn-link-inline"
                    on:click={() => clearMissingPiecesForItem(order, item)}
                    disabled={clearing[itemKey(order.id, item.order_product_id)]}
                    title="Remove all missing-piece attributions on this item"
                  >
                    {clearing[itemKey(order.id, item.order_product_id)] ? "Clearing…" : "Clear missing pieces"}
                  </button>
                {/if}
              {:else}
                <span class="hint">—</span>
              {/if}
            </td>
            <td class="pick-cell">
              {#if item}
                <input
                  type="radio"
                  name="picker-target"
                  value={itemKey(order.id, item.order_product_id)}
                  bind:group={targetKey}
                  on:change={() => clearOppositeIfSame("target", itemKey(order.id, item.order_product_id))}
                />
              {:else}
                <span class="hint">—</span>
              {/if}
            </td>
            <td class="pick-cell">
              {#if item}
                <input
                  type="radio"
                  name="picker-source"
                  value={itemKey(order.id, item.order_product_id)}
                  bind:group={sourceKey}
                  on:change={() => clearOppositeIfSame("source", itemKey(order.id, item.order_product_id))}
                />
              {:else}
                <span class="hint">—</span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
    </div>
  {/if}
</section>

<style type="text/less">
  section {
    padding: var(--spacing-sm);
  }

  h3 {
    margin-top: var(--spacing);
  }

  .search-form {
    border: 1px solid var(--col-gray-80);
    padding: var(--spacing-sm);
    margin-bottom: var(--spacing);

    h3 {
      margin-top: 0;
    }
  }

  .form-row {
    display: flex;
    gap: 10px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .hint {
    color: var(--col-gray-40);
    font-size: 0.85em;
  }

  .selection-bar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: var(--spacing-sm);
    background: var(--col-gray-95, var(--col-gray-90));
    border-radius: 4px;
    margin-bottom: var(--spacing);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .selection-row {
    display: flex;
    gap: var(--spacing);
    align-items: center;
    flex-wrap: wrap;
  }

  .submit-row {
    gap: 12px;
    padding-top: 8px;
    border-top: 1px solid var(--col-gray-90);
  }

  .selection-label {
    font-weight: 600;
    color: var(--col-gray-30);
    min-width: 160px;
  }

  .selection-value {
    color: var(--col-gray-10, var(--col-gray-20));
    font-weight: 500;
  }

  .selection-empty {
    color: var(--col-gray-50);
    font-style: italic;
  }

  .selection-clear {
    appearance: none;
    background: transparent;
    border: none;
    color: var(--col-gray-40);
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 50%;

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      color: var(--col-gray-20);
    }
  }

  .count-input {
    width: 70px;
  }

  .conditions-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .btn-link-inline {
    appearance: none;
    background: none;
    border: none;
    padding: 2px 0;
    margin: 0;
    color: var(--col-danger);
    font-size: 0.85em;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: var(--col-danger-d);
    }

    &:disabled {
      color: var(--col-gray-40);
      cursor: default;
      text-decoration: none;
    }
  }

  // Match the score-badge style used in AdminOrders / ScoreBadge.svelte.
  .score-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border-radius: var(--border-radius);
    font-size: small;
    font-weight: 600;
    color: #fff;
  }

  .score-badge :global(.icon) {
    filter: brightness(0) invert(1);
  }

  .score-badge-warning {
    background: var(--col-warning);
  }

  .product-preview {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: var(--spacing-sm);
    padding: var(--spacing-sm);
    background: var(--col-gray-95, var(--col-gray-90));
    border-radius: 4px;
  }

  .product-image {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .product-meta p {
    margin: 0;
  }

  .product-title {
    font-weight: 600;
  }

  .pick-cell {
    text-align: center;
  }

  .orders-table-wrap {
    overflow-x: auto;
    border: 1px solid var(--col-gray-80);
    border-radius: 4px;
    background: white;
    margin-top: var(--spacing-sm);
  }

  .orders-table {
    width: 100%;
    margin: 0;
    border-collapse: collapse;
    font-size: 0.9em;
  }

  .orders-table th {
    position: sticky;
    top: 0;
    background: var(--col-gray-95, var(--col-gray-90));
    color: var(--col-gray-30);
    font-weight: 600;
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid var(--col-gray-80);
    white-space: nowrap;
  }

  .orders-table td {
    padding: 8px 10px;
    vertical-align: middle;
    border-bottom: 1px solid var(--col-gray-95, var(--col-gray-90));
  }

  .orders-table tbody tr:nth-child(even) td {
    background: var(--col-gray-98, #fafafa);
  }

  .orders-table tbody tr:hover td {
    background: var(--col-gray-95, var(--col-gray-90));
  }

  .orders-table .col-order {
    font-weight: 600;
    white-space: nowrap;
  }

  .orders-table .col-customer {
    white-space: nowrap;
  }

  .orders-table .col-status {
    white-space: nowrap;
    color: var(--col-gray-30);
  }

  .orders-table .col-date {
    white-space: nowrap;
    color: var(--col-gray-30);
  }

  .orders-table .col-num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .orders-table th.col-num {
    text-align: right;
  }

  .orders-table .pick-cell {
    text-align: center;
    padding: 8px 16px;
  }
</style>
